import { Button, ModalDialog, ModalOverlay } from '@flos/react-ui';
import { useHippoText } from '../../hooks/useHippoText';
import type { OnsaStep } from '../../recoil/types/onsaTypes';
import type { ConflictingCoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { useRecoilValue } from 'recoil';
import { allCoverablesOnsaToCoverableDTOArray } from '../../utils/coverablesUtil';
import { allCoverablesOnsaAtom } from '../../recoil/atoms/allCoverablesOnsaAtom';
import DOMPurify from 'dompurify';

const MergeBasketModal = ({
    showModal,
    closeModal,
    nextPage,
    conflictingCoverables
}: {
    showModal: boolean;
    closeModal: () => void;
    nextPage: OnsaStep;
    conflictingCoverables: ConflictingCoverableDTO[];
}): React.JSX.Element => {
    const getHippoText = useHippoText();

    const allCoverablesOnsa = useRecoilValue(allCoverablesOnsaAtom);
    const onlyOneOnsaCoverable =
        allCoverablesOnsaToCoverableDTOArray(allCoverablesOnsa).length <= 1;

    const getInsurancesText = (conflictingCoverables: ConflictingCoverableDTO[]): string => {
        let insuranceNames = '';
        conflictingCoverables.forEach((conflictingCoverable) => {
            insuranceNames += `<li>${conflictingCoverable.flavourName ?? ''} (${conflictingCoverable.name ?? ''})</li>`;
        });
        return `<ul>${insuranceNames}</ul>`;
    };

    const headerAndDescriptionTexts =
        conflictingCoverables.length === 0
            ? onlyOneOnsaCoverable
                ? {
                      header: getHippoText('DSS-EXISTING-BASKET-MODAL-HEADER-1-1'),
                      description: getHippoText('DSS-EXISTING-BASKET-MODAL-DESCRIPTION-1-1')
                  }
                : {
                      header: getHippoText('DSS-EXISTING-BASKET-MODAL-HEADER-1-2'),
                      description: getHippoText('DSS-EXISTING-BASKET-MODAL-DESCRIPTION-1-2')
                  }
            : conflictingCoverables.length === 1
              ? {
                    header: getHippoText('DSS-EXISTING-BASKET-MODAL-HEADER-2-1', {
                        insurance: conflictingCoverables[0].flavourName?.toLowerCase() ?? ''
                    }),
                    description: getHippoText('DSS-EXISTING-BASKET-MODAL-DESCRIPTION-2-1', {
                        insurance: conflictingCoverables[0].flavourName?.toLowerCase() ?? '',
                        coverable: conflictingCoverables[0].name ?? ''
                    })
                }
              : {
                    header: getHippoText('DSS-EXISTING-BASKET-MODAL-HEADER-2-2'),
                    description: getHippoText('DSS-EXISTING-BASKET-MODAL-DESCRIPTION-2-2', {
                        insurance_list: getInsurancesText(conflictingCoverables)
                    })
                };

    const continueButtonText =
        nextPage === 'ShoppingBasketPage'
            ? getHippoText('DSS-EXISTING-BASKET-MODAL-SHOPPING-BASKET')
            : getHippoText('DSS-EXISTING-BASKET-MODAL-CONTINUE');

    return (
        <ModalOverlay isOpen={showModal}>
            <ModalDialog closeIconHandler={closeModal}>
                <div className="u-spacing-inset-l">
                    <div className="u-spacing-inset-l--stretch-max">
                        <h4 className="u-spacing-stack-sm">{headerAndDescriptionTexts.header}</h4>
                        <p
                            className="u-spacing-stack-sm"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(headerAndDescriptionTexts.description)
                            }}
                        />
                    </div>
                    <Button onClick={closeModal}>{continueButtonText}</Button>
                </div>
            </ModalDialog>
        </ModalOverlay>
    );
};

export default MergeBasketModal;
